(() => {
  if (window.__eamdQuickViewLoaded) return;
  window.__eamdQuickViewLoaded = true;

  // Quick-view host — ONE per store, opened by `quickview:open`
  // (`digitdeck-shopify/quick-view-drawer-standard.md`, reference: NOS Café
  // `QuickViewHost.jsx`).
  //
  // Camino B, and here is WHY, measured rather than assumed: this theme ships its
  // buy box as a compiled bundle with no source, no sourcemap and no build. The
  // bundle registers islands once and never rescans (0 MutationObserver, no
  // `shopify:section:load` hook), so an island inserted into a drawer would never
  // hydrate. Camino A returns once the theme has its own build (issue #52).
  //
  // What this DOES keep from Camino A: there is **no second data contract**. The
  // drawer fetches the product PAGE and lifts the PDP's own serialization off the
  // island's JSON payload, exactly like NOS does — so any change to what Liquid
  // serializes shows up here for free.
  const CACHE = new Map();

  const config = () => window.__EAMD_CONFIG__ || {};
  const money = (cents) => {
    const locale = config().localeCode || document.documentElement.lang || 'es-CO';
    const currency = config().currencyCode || 'COP';
    // The cards print "85.000 COP", so the drawer prints the same. `currencyDisplay:
    // 'narrowSymbol'` renders "85.000 $", which reads as USD next to a COP card.
    const amount = Number(cents || 0) / 100;
    try {
      return `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(amount)} ${currency}`;
    } catch (_) {
      return `${Math.round(amount).toLocaleString('es-CO')} ${currency}`;
    }
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const loadProduct = async (handle) => {
    if (CACHE.has(handle)) return CACHE.get(handle);
    const res = await fetch(`${config().rootUrl || '/'}products/${handle}`.replace('//products', '/products'),
      { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`quick-view: ${handle} -> ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    // La ficha nueva (rail dinámico, #52) serializa en español; la heredada en inglés. Se
    // acepta cualquiera de las dos y se normaliza a la forma que pinta este cajón.
    const nuevo = doc.querySelector('[data-component="ConfiguradorProductoIsland"] script[type="application/json"]');
    const payload = nuevo || doc.querySelector('[data-component="ProductDetailIsland"] script[type="application/json"]');
    if (!payload) throw new Error(`quick-view: ${handle} sin payload`);
    const crudo = JSON.parse(payload.textContent);
    const data = nuevo ? normalizarNuevo(crudo) : crudo;
    CACHE.set(handle, data);
    return data;
  };

  const normalizarNuevo = (d) => ({
    product: { title: d.producto?.titulo, handle: d.producto?.handle, url: d.producto?.url },
    options: (d.opciones || []).map((o) => ({ name: o.nombre, values: (o.valores || []).map((v) => v.valor) })),
    variants: (d.variantes || []).map((v) => ({ id: v.id, price: v.precio, available: v.disponible, options: v.opciones })),
    media: (d.imagenes || []).map((m) => ({ src: m.src, alt: m.alt })),
    // El plan lo decide la ficha: iniciales, propiedades del perfil o receta de compuesto.
    personalizable: Boolean(d.perfil) || Boolean(d.receta) || /\b(1|una|2|dos)?\s*inicial(es)?\b/i.test(d.producto?.titulo || '')
  });

  // A product whose PDP asks for line properties (initials, chain length) cannot be
  // completed from here: those finish on the PDP. With the new rail the flag travels in
  // the payload; the title regex only remains for the legacy island.
  const needsPersonalisation = (data) => (typeof data?.personalizable === 'boolean' ? data.personalizable : /inicial|personaliz/i.test(data?.product?.title || ''));

  const buyableVariants = (data) => (data.variants || []).filter((v) => v.available);

  // The cart drawer's store lives INSIDE the compiled bundle: it is a module-level
  // singleton, exposed on no global, and the bundle listens for zero custom events
  // (measured: `addEventListener` appears 4 times, all of them "scroll"/"resize"/
  // "DOMContentLoaded"/"test"). So an add made from out here cannot notify it. Its
  // `fetchCart()` runs on mount, so a reload rehydrates the correct cart — that is the
  // honest way to finish, and it goes away when the island is rebuilt from source (#52).
  const addLines = async (items) => {
    const res = await fetch(config().routes?.cartAddJson || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) throw new Error('cart/add failed');
    await res.json();
    window.location.reload();
  };

  const matchVariant = (data, chosen) => (data.variants || []).find((variant) =>
    (variant.options || []).every((value, index) => value === chosen[index]));

  let root = null;
  let lastTrigger = null;

  const build = () => {
    const host = el('div', 'eamd-qv');
    host.dataset.eamdQuickView = 'true';
    host.hidden = true;
    host.innerHTML = `
      <div class="eamd-qv__scrim" data-eamd-qv-close></div>
      <aside class="eamd-qv__panel" role="dialog" aria-modal="true" aria-label="Vista rápida" tabindex="-1" data-lenis-prevent>
        <button class="eamd-qv__close" type="button" data-eamd-qv-close aria-label="Cerrar">&times;</button>
        <div class="eamd-qv__body" data-eamd-qv-body></div>
      </aside>`;
    document.body.append(host);
    host.addEventListener('click', (event) => {
      if (event.target.closest('[data-eamd-qv-close]')) close();
    });
    return host;
  };

  const open = () => {
    root.hidden = false;
    document.body.classList.add('eamd-qv-open');
    requestAnimationFrame(() => {
      root.classList.add('is-open');
      root.querySelector('.eamd-qv__panel').focus();
    });
  };

  const close = () => {
    root.classList.remove('is-open');
    document.body.classList.remove('eamd-qv-open');
    setTimeout(() => { root.hidden = true; }, 240);
    lastTrigger?.focus();
  };

  const render = (data) => {
    const body = root.querySelector('[data-eamd-qv-body]');
    body.replaceChildren();

    const media = (data.media || [])[0];
    if (media) {
      const figure = el('div', 'eamd-qv__media');
      const img = document.createElement('img');
      img.src = media.src;
      img.alt = media.alt || data.product?.title || '';
      figure.append(img);
      body.append(figure);
    }

    body.append(el('h2', 'eamd-qv__title', data.product?.title || ''));
    const price = el('p', 'eamd-qv__price');
    body.append(price);

    // One control group per REAL option of this product, discovered from the payload.
    const chosen = [];
    const groups = [];
    (data.options || []).forEach((option, index) => {
      const values = option.values || [];
      chosen[index] = values[0];
      const group = el('div', 'eamd-qv__group');
      const label = el('div', 'eamd-qv__group-label');
      label.append(el('span', 'eamd-qv__group-caption', option.name),
        el('span', 'eamd-qv__group-value', values[0] || ''));
      const row = el('div', 'eamd-qv__values');
      values.forEach((value) => {
        const button = el('button', 'eamd-qv__value', value);
        button.type = 'button';
        button.dataset.value = value;
        button.addEventListener('click', () => {
          chosen[index] = value;
          sync();
        });
        row.append(button);
      });
      group.append(label, row);
      groups.push({ group, index, values });
      body.append(group);
    });

    const cta = el('button', 'eamd-qv__cta');
    cta.type = 'button';
    const note = el('p', 'eamd-qv__note');
    const full = el('a', 'eamd-qv__full', 'Ver producto completo');
    full.href = data.product?.url || `/products/${data.product?.handle || ''}`;
    body.append(cta, note, full);

    const personalised = needsPersonalisation(data);

    function sync() {
      groups.forEach(({ group, index }) => {
        group.querySelector('.eamd-qv__group-value').textContent = chosen[index] || '';
        group.querySelectorAll('[data-value]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.value === chosen[index]));
        });
      });
      const variant = matchVariant(data, chosen);
      price.textContent = variant ? money(variant.price) : '';
      if (personalised) {
        cta.textContent = 'Personalizar';
        note.textContent = 'Este producto se personaliza en su página.';
      } else {
        cta.textContent = variant && variant.available ? 'Agregar al carrito' : 'Agotado';
        cta.disabled = !(variant && variant.available);
        note.textContent = '';
      }
      cta.dataset.variantId = variant ? String(variant.id) : '';
    }

    cta.addEventListener('click', async () => {
      if (personalised) { window.location.href = full.href; return; }
      const id = Number(cta.dataset.variantId);
      if (!id) return;
      cta.disabled = true;
      cta.textContent = 'Agregando…';
      try {
        await addLines([{ id, quantity: 1 }]);
      } catch (_) {
        note.textContent = 'No pudimos agregarlo. Intenta de nuevo.';
      } finally {
        cta.disabled = false;
      }
    });

    sync();
  };

  const openFor = async (handle, trigger) => {
    lastTrigger = trigger || null;
    if (!root) root = build();
    const body = root.querySelector('[data-eamd-qv-body]');
    body.replaceChildren(el('p', 'eamd-qv__loading', 'Cargando…'));
    open();
    try {
      const data = await loadProduct(handle);
      // Convention: a product with a single buyable variant and no personalisation
      // adds directly — opening a drawer with no decision inside is pure friction.
      if (!needsPersonalisation(data) && buyableVariants(data).length === 1) {
        await addLines([{ id: buyableVariants(data)[0].id, quantity: 1 }]);
        return;
      }
      render(data);
    } catch (error) {
      body.replaceChildren(el('p', 'eamd-qv__loading', 'No pudimos cargar este producto.'));
      console.error(error);
    }
  };

  document.addEventListener('quickview:open', (event) => {
    const detail = event.detail || {};
    const handle = detail.handle
      || (typeof detail.url === 'string' ? detail.url.split('/products/')[1]?.split(/[?#]/)[0] : null);
    if (!handle) return;
    // Tell the opener we took it, so the card does not fall back to navigation.
    event.preventDefault();
    openFor(handle, detail.trigger || document.activeElement);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root && !root.hidden) close();
  });
})();
